import { useState, useEffect, useCallback, useRef } from "react";
import {
  getAllFarmsSeoAdmin,
  updateFarmSeoAdmin,
} from "../../../../api/user-service/adminService";
import styles from "../../../../assets/stylesheets/dashboard/seo/FarmSeoAdmin.module.css";
import DashboardLayout from "../../DashboardLayout";

const LIMIT = 20;
const META_TITLE_MAX = 100;
const META_DESC_MAX = 200;
const DESC_PREVIEW_LEN = 100;

/* ── Description  ─────────────────────────────── */
function DescCell({ text }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return <span style={{ color: "#d1d5db", fontSize: "0.8rem" }}>—</span>;
  const isLong = text.length > DESC_PREVIEW_LEN;
  return (
    <div className={styles.descWrap}>
      <span className={styles.descText}>
        {isLong && !expanded ? text.slice(0, DESC_PREVIEW_LEN) + "…" : text}
      </span>
      {isLong && (
        <button
          className={styles.descToggleBtn}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "▲" : "More"}
        </button>
      )}
    </div>
  );
}

/* ── Main Page ───────────────────────────────────────────── */
export default function FarmSeoPage() {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [search, setSearch] = useState("");
  const searchDebounce = useRef(null);

  // ── Fetch ────────────────────────────────────────────────
  const fetchPage = useCallback(async (pageNum) => {
    setLoading(true);
    setFetchError(null);
    try {
      const res = await getAllFarmsSeoAdmin({ page: pageNum, limit: LIMIT });
      // { success, data: { data:[...], total, page, limit }, meta }
      const outer   = res?.data ?? res;
      const payload = outer?.data ?? outer;
      const items   = Array.isArray(payload)
        ? payload
        : (payload.data ?? payload.items ?? payload.farms ?? []);
      const total = payload.total ?? payload.totalCount ?? items.length;
      const pages = payload.totalPages ?? Math.ceil(total / LIMIT) ?? 1;

      if (!Array.isArray(items)) {
        console.error("[FarmSeoPage] items is not an array:", items);
        setFetchError("Unexpected response format from server.");
        return;
      }

      setRows(
        items.map((farm) => {
          const mt  = farm.metaTitle ?? "";
          const md  = farm.metaDescription ?? "";
          const hasSaved = mt.trim() !== "" || md.trim() !== "";
          return {
            farmId:          farm.id ?? farm.farmId,
            name:            farm.name ?? farm.farmName ?? "—",
            description:     farm.description ?? "",
            metaTitle:       mt,
            metaDescription: md,
            editMetaTitle:   mt,
            editMetaDescription: md,
            editing: !hasSaved,
            saving:  false,
            saved:   false,
            error:   null,
          };
        })
      );
      setTotalItems(total);
      setTotalPages(pages);
    } catch (err) {
      setFetchError(
        err?.response?.data?.message ?? err?.message ?? "Failed to load SEO data."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPage(page); }, [page, fetchPage]);

  // ── Search ───────────────────────────────────────────────
  const handleSearchChange = (e) => {
    clearTimeout(searchDebounce.current);
    const val = e.target.value;
    searchDebounce.current = setTimeout(() => setSearch(val), 250);
  };

  const filteredRows = search.trim()
    ? rows.filter((r) => r.name.toLowerCase().includes(search.toLowerCase()))
    : rows;

  // ── Field update ─────────────────────────────────────────
  const updateField = (farmId, field, value) => {
    setRows((prev) =>
      prev.map((r) =>
        r.farmId === farmId ? { ...r, [field]: value } : r
      )
    );
  };

  // ── Enter / Cancel edit ──────────────────────────────────
  const startEdit = (farmId) => {
    setRows((prev) =>
      prev.map((r) =>
        r.farmId === farmId
          ? { ...r, editing: true, editMetaTitle: r.metaTitle, editMetaDescription: r.metaDescription, saved: false, error: null }
          : r
      )
    );
  };

  const cancelEdit = (farmId) => {
    setRows((prev) =>
      prev.map((r) =>
        r.farmId === farmId ? { ...r, editing: false, error: null } : r
      )
    );
  };

  // ── Save row ─────────────────────────────────────────────
  const saveRow = async (farmId) => {
    const row = rows.find((r) => r.farmId === farmId);
    if (!row) return;

    setRows((prev) =>
      prev.map((r) =>
        r.farmId === farmId ? { ...r, saving: true, saved: false, error: null } : r
      )
    );

    try {
      await updateFarmSeoAdmin(farmId, {
        metaTitle:       row.editMetaTitle.trim(),
        metaDescription: row.editMetaDescription.trim(),
      });
      setRows((prev) =>
        prev.map((r) =>
          r.farmId === farmId
            ? { ...r, saving: false, saved: true, editing: false, metaTitle: r.editMetaTitle.trim(), metaDescription: r.editMetaDescription.trim() }
            : r
        )
      );
      setTimeout(() => {
        setRows((prev) =>
          prev.map((r) =>
            r.farmId === farmId ? { ...r, saved: false } : r
          )
        );
      }, 2500);
    } catch (err) {
      setRows((prev) =>
        prev.map((r) =>
          r.farmId === farmId
            ? { ...r, saving: false, error: "Save failed. Try again." }
            : r
        )
      );
    }
  };

  const charClass = (len, max) =>
    len > max
      ? `${styles.charCount} ${styles.charCountOver}`
      : styles.charCount;

  const Pagination = ({ mt }) => (
    <div className={styles.toolbar} style={{ marginTop: mt, justifyContent: "flex-end" }}>
      <span className={styles.paginationInfo}>
        Page {page} / {totalPages}
      </span>
      <div className={styles.paginationBtns}>
        <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading}>
          ← Prev
        </button>
        <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || loading}>
          Next →
        </button>
      </div>
    </div>
  );

  // ── Render ───────────────────────────────────────────────
  return (
    <DashboardLayout>
      <div className={styles.page}>
        {/* Header */}
        <div className={styles.header}>
          <h1>Farm SEO Manager</h1>

        </div>

        {/* Error banner */}
        {fetchError && (
          <div className={`${styles.statusBanner} ${styles.statusError}`}>
            <span>⚠ {fetchError}</span>
            <button
              onClick={() => fetchPage(page)}
              style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Toolbar */}
        <div className={styles.toolbar}>
          <div className={styles.searchWrap}>
            <span className={styles.searchIcon}>🔍</span>
            <input
              className={styles.searchInput}
              type="search"
              placeholder="Search Farm Name"
              onChange={handleSearchChange}
              aria-label="Search farms"
            />
          </div>
          <span className={styles.paginationInfo}>
            {loading
              ? "Loading…"
              : `Total ${totalItems} farms · Page ${page} / ${totalPages}`}
          </span>
          <div className={styles.paginationBtns}>
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading}>
              ← Prev
            </button>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || loading}>
              Next →
            </button>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className={styles.loading}>
            <span className={styles.spinner} /> Loading farms…
          </div>
        ) : filteredRows.length === 0 ? (
          <div className={styles.empty}>No farms found.</div>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: "15%" }}>Farm</th>
                  <th style={{ width: "22%" }}>Description</th>
                  <th style={{ width: "25%" }}>Meta Title</th>
                  <th style={{ width: "28%" }}>Meta Description</th>
                  <th style={{ width: "10%" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={row.farmId}>
                    {/* Farm name */}
                    <td>
                      <div className={styles.farmName}>{row.name}</div>
                    </td>

                    {/* Description */}
                    <td>
                      <DescCell text={row.description} />
                    </td>

                    {/* Meta title */}
                    <td>
                      {row.editing ? (
                        <>
                          <input
                            className={styles.seoInput}
                            type="text"
                            value={row.editMetaTitle}
                            maxLength={META_TITLE_MAX + 10}
                            placeholder={`${row.name} | Farmciti`}
                            onChange={(e) => updateField(row.farmId, "editMetaTitle", e.target.value)}
                          />
                          <div className={charClass(row.editMetaTitle.length, META_TITLE_MAX)}>
                            {row.editMetaTitle.length}/{META_TITLE_MAX}
                          </div>
                        </>
                      ) : (
                        <div className={styles.seoValueDisplay}>
                          <span>{row.metaTitle}</span>
                        </div>
                      )}
                    </td>

                    {/* Meta description */}
                    <td>
                      {row.editing ? (
                        <>
                          <textarea
                            className={styles.seoInput}
                            rows={3}
                            value={row.editMetaDescription}
                            maxLength={META_DESC_MAX + 20}
                            placeholder="meta description..."
                            onChange={(e) => updateField(row.farmId, "editMetaDescription", e.target.value)}
                            style={{ minHeight: "72px" }}
                          />
                          <div className={charClass(row.editMetaDescription.length, META_DESC_MAX)}>
                            {row.editMetaDescription.length}/{META_DESC_MAX}
                          </div>
                          {row.error && (
                            <div style={{ color: "#ef4444", fontSize: "0.78rem", marginTop: 3 }}>
                              {row.error}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className={styles.seoValueDisplay}>
                          <span>{row.metaDescription}</span>
                        </div>
                      )}
                    </td>

                    {/* Action */}
                    <td>
                      {row.saved ? (
                        <span className={styles.savedLabel}>✓ Saved</span>
                      ) : row.editing ? (
                        <div className={styles.actionBtns}>
                          <button
                            className={styles.saveBtn}
                            onClick={() => saveRow(row.farmId)}
                            disabled={row.saving}
                          >
                            {row.saving ? (
                              <><span className={styles.spinner} /> Saving…</>
                            ) : (
                              "Save"
                            )}
                          </button>
                          {(row.metaTitle || row.metaDescription) && (
                            <button
                              className={styles.cancelBtn}
                              onClick={() => cancelEdit(row.farmId)}
                              disabled={row.saving}
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      ) : (
                        <button
                          className={styles.editBtn}
                          onClick={() => startEdit(row.farmId)}
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Bottom pagination */}
        {!loading && totalPages > 1 && <Pagination mt={20} />}
      </div>
    </DashboardLayout>
  );
}
